import { type FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { GoogleSignIn } from "../../components/GoogleSignIn";
import { Input } from "../../components/Input";
import { Logo } from "../../components/Logo";
import { Body, Heading, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import loginStyles from "../Login/Login.module.css";

type VerifyState = { verifyEmail?: string; verifyPassword?: string };

export const Register = () => {
  const { status, register, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const verifyState = (location.state as VerifyState | null) ?? {};
  const [email, setEmail] = useState(verifyState.verifyEmail ?? "");
  const [password, setPassword] = useState(verifyState.verifyPassword ?? "");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState<"form" | "verify">(
    verifyState.verifyEmail ? "verify" : "form",
  );
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);

  if (status === "authenticated") return <Navigate to="/stores" replace />;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register(email, password, displayName.trim() || null);
      setPhase("verify");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (e.body?.error ?? e.message)
          : "Couldn't create your account. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.verifyRegistration(email, code.trim());
      if (password) {
        await login(email, password);
        navigate("/stores", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't verify. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    setResent(false);
    await api.resendRegistrationCode(email);
    setResent(true);
  };

  if (phase === "verify") {
    return (
      <main className={loginStyles.page}>
        <div className={loginStyles.card}>
          <div className={loginStyles.brand}>
            <Logo />
          </div>
          <div className={loginStyles.header}>
            <Heading level={2}>Check your email</Heading>
            <Body>
              <Muted>We sent a verification code to {email}.</Muted>
            </Body>
          </div>

          <form className={loginStyles.form} onSubmit={handleVerify} noValidate>
            <div className={loginStyles.field}>
              <label className={loginStyles.label} htmlFor="code">
                Verification code
              </label>
              <Input
                id="code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={submitting}
                invalid={error !== null}
              />
            </div>
            {error && (
              <div role="alert" className={loginStyles.error}>
                {error}
              </div>
            )}
            {resent && (
              <Body>
                <Muted>A new code is on its way.</Muted>
              </Body>
            )}
            <Button type="submit" disabled={submitting || code.trim().length === 0}>
              {submitting ? "Verifying…" : "Verify email"}
            </Button>
          </form>

          <div className={loginStyles.footer}>
            Didn't get it?{" "}
            <button type="button" className={loginStyles.linkButton} onClick={handleResend}>
              Resend code
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={loginStyles.page}>
      <div className={loginStyles.card}>
        <div className={loginStyles.brand}>
          <Logo />
        </div>
        <div className={loginStyles.header}>
          <Heading level={2}>Create your account</Heading>
          <Body>
            <Muted>Takes under a minute.</Muted>
          </Body>
        </div>

        <GoogleSignIn
          text="signup_with"
          onSignedIn={() => navigate("/stores", { replace: true })}
          onEmailUnverified={(address) => {
            // A Google account on a non-Gmail address: verify the address first, then
            // sign in with Google again (no password to sign in with here).
            setEmail(address);
            setPassword("");
            setPhase("verify");
          }}
        />

        <form className={loginStyles.form} onSubmit={handleSubmit} noValidate>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="display_name">
              Name
            </label>
            <Input
              id="display_name"
              name="display_name"
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              invalid={error !== null}
            />
          </div>
          <div className={loginStyles.field}>
            <label className={loginStyles.label} htmlFor="password">
              Password
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              invalid={error !== null}
            />
          </div>
          {error && (
            <div role="alert" className={loginStyles.error}>
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <div className={loginStyles.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </main>
  );
};
